import React, { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

// Logo QUINLATE SHOP recortado en 3D (Three.js), sin disco ni fondo: el lienzo es transparente
// y se ve la página a través de los huecos. Reemplaza a los dos videos del logo que había en la
// Landing (uno por tema, con mix-blend-mode) — un MP4 no puede tener fondo transparente.
// El material sigue al tema, igual que hacían esos videos:
//   oscuro → plata cromada · claro → negro laca
// Se arrastra en los dos ejes (mouse o dedo) y, si nadie lo toca, gira solo.
// Three.js y las formas del logo se cargan aparte (import dinámico), solo cuando se monta.
// Probado en el demo de NEGOCIOS (demos-3d/logo-quinlate-3d-solo) antes de pasarlo aquí.

const SENS = 0.45;       // grados por pixel arrastrado
const MAX_X = 65;        // límite de inclinación arriba/abajo
const SPIN = 20;         // giro automático, grados por segundo
const IDLE_MS = 1000;    // espera al soltarlo antes de volver a su posición y girar solo
const TILT = 10;         // inclinación de reposo: se ve el grosor de arriba
const DEPTH = 0.11;      // grosor del logo (el anillo mide radio 1)
const FADE_MS = 500;     // transición de material al cambiar de tema
const RAD = Math.PI / 180;
const clampX = (x) => Math.max(-MAX_X, Math.min(MAX_X, x));

const createEngine = (el, THREE, toCreasedNormals, shapesData, initialTheme) => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });   // sin WebGL lanza error
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.05;
  el.appendChild(renderer.domElement);
  const scene = new THREE.Scene();

  // Dos estudios de reflejos: el cromo necesita una luz grande de frente (si no, de frente se ve
  // negro) y la laca negra no, porque esa misma luz la vuelve gris.
  const studio = (withFront) => {
    const s = new THREE.Scene();
    s.background = new THREE.Color(0x050505);
    const softbox = (w, h, x, y, z, power) => {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(power, power, power), side: THREE.DoubleSide })
      );
      m.position.set(x, y, z);
      m.lookAt(0, 0, 0);
      s.add(m);
    };
    if (withFront) softbox(10, 9, 0, -1.5, 7, 1.8);   // alta hacia abajo: SHOP refleja más abajo que QUINLATE
    else softbox(8, 0.6, 0, 3.2, 6, 3);               // laca: solo un filo de luz arriba-frente
    softbox(9, 1.4, 0, 7, -1, 5);
    softbox(1.4, 8, -7, 0, 1, 4);
    softbox(1.4, 8, 6, -1, -4, 3);                     // contraluz al girar
    return s;
  };
  const pmrem = new THREE.PMREMGenerator(renderer);
  const env = {};
  ['dark', 'light'].forEach((t) => {
    const s = studio(t === 'dark');
    env[t] = pmrem.fromScene(s, 0.02).texture;
    s.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); } });
  });

  const LOOK = {
    dark: { color: new THREE.Color(0xdcd8d0), metalness: 1, roughness: 0.14 },
    light: { color: new THREE.Color(0x0a0a0a), metalness: 0, roughness: 0.1 },
  };
  const mat = new THREE.MeshPhysicalMaterial({ specularIntensity: 1 });
  const key = new THREE.DirectionalLight(0xfff4e0, 1.2);
  key.position.set(-2.5, 3, 4);
  const rim = new THREE.DirectionalLight(0xffffff, 0.6);
  rim.position.set(3, -1, -3);
  scene.add(key, rim);

  const applyLook = (from, to, t) => {
    const a = LOOK[from];
    const b = LOOK[to];
    mat.color.copy(a.color).lerp(b.color, t);
    mat.metalness = a.metalness + (b.metalness - a.metalness) * t;
    mat.roughness = a.roughness + (b.roughness - a.roughness) * t;
    scene.environment = env[t < 0.5 ? from : to];   // el cambio de reflejos cae cuando el color va en gris
  };
  let theme = initialTheme === 'light' ? 'light' : 'dark';
  applyLook(theme, theme, 1);
  let fade = null;

  const toVecs = (flat) => {
    const v = [];
    for (let i = 0; i < flat.length; i += 2) v.push(new THREE.Vector2(flat[i], flat[i + 1]));
    return v;
  };
  const shapes = shapesData.map((s) => {
    const sh = new THREE.Shape(toVecs(s.o));
    sh.holes = s.h.map((h) => new THREE.Path(toVecs(h)));
    return sh;
  });
  let geo = new THREE.ExtrudeGeometry(shapes, {
    depth: DEPTH,
    curveSegments: 4,
    bevelEnabled: true,
    bevelThickness: 0.008,
    bevelSize: 0.003,
    bevelOffset: -0.003,
    bevelSegments: 3,
  });
  const extruded = geo;
  geo = toCreasedNormals(geo, 35 * RAD);   // curvas lisas en los reflejos, esquinas marcadas
  extruded.dispose();
  geo.computeBoundingBox();
  const c = geo.boundingBox.getCenter(new THREE.Vector3());
  geo.translate(-c.x, -c.y, -c.z);
  geo.computeBoundingSphere();
  const logo = new THREE.Mesh(geo, mat);
  scene.add(logo);

  const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 50);
  camera.position.z = (geo.boundingSphere.radius * 1.12) / Math.tan(13 * RAD);

  let rx = TILT;
  let ry = -25;
  let vx = 0;
  let vy = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let lastMove = 0;
  let idleSince = -IDLE_MS;
  let visible = true;
  let raf = 0;
  let prev = performance.now();

  const render = () => {
    logo.rotation.set(rx * RAD, ry * RAD, 0);
    renderer.render(scene, camera);
  };

  const tick = (now) => {
    raf = 0;
    if (!visible) return;
    const dt = Math.min(now - prev, 64);
    const k = dt / 16.67;
    prev = now;
    if (!dragging) {
      ry += vy * k;
      rx = clampX(rx + vx * k);
      vx *= Math.pow(0.88, k);
      if (!reduceMotion && now - idleSince > IDLE_MS) {
        vy += (SPIN / 60 - vy) * 0.06 * k;
        rx += (TILT - rx) * 0.05 * k;
      } else {
        vy *= Math.pow(0.94, k);   // la fricción solo frena el impulso de la mano, no el giro automático
      }
    }
    if (fade) {
      const t = Math.min((now - fade.start) / FADE_MS, 1);
      applyLook(fade.from, fade.to, t * t * (3 - 2 * t));
      if (t === 1) fade = null;
    }
    render();
    if (dragging || fade || !reduceMotion || Math.abs(vx) + Math.abs(vy) > 0.01) raf = requestAnimationFrame(tick);
  };
  const wake = () => {
    if (visible && !raf) {
      prev = performance.now();
      raf = requestAnimationFrame(tick);
    }
  };

  const resize = () => {
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    wake();
  };
  const ro = new ResizeObserver(resize);
  ro.observe(el);
  const io = new IntersectionObserver(([en]) => {
    visible = en.isIntersecting;
    wake();
  });
  io.observe(el);

  const onDown = (e) => {
    if (e.button !== 0) return;
    if (e.pointerType === 'mouse') e.preventDefault();
    dragging = true;
    vx = 0;
    vy = 0;
    lastX = e.clientX;
    lastY = e.clientY;
    lastMove = performance.now();
    try { el.setPointerCapture(e.pointerId); } catch (_) { /* sin captura igual sigue por window */ }
    el.classList.add('is-dragging');
    el.focus({ preventScroll: true });
    wake();
  };
  const onMove = (e) => {
    if (!dragging) return;
    const now = performance.now();
    const dtm = Math.max(now - lastMove, 8);
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    ry += dx * SENS;
    rx = clampX(rx + dy * SENS);
    vy = vy * 0.5 + ((dx * SENS) / dtm) * 16.67 * 0.5;
    vx = vx * 0.5 + ((dy * SENS) / dtm) * 16.67 * 0.5;
    lastX = e.clientX;
    lastY = e.clientY;
    lastMove = now;
  };
  const onUp = () => {
    if (!dragging) return;
    dragging = false;
    if (performance.now() - lastMove > 90) { vx = 0; vy = 0; }
    idleSince = performance.now();
    el.classList.remove('is-dragging');
  };
  const onKey = (e) => {
    const step = { ArrowLeft: [0, -15], ArrowRight: [0, 15], ArrowUp: [-10, 0], ArrowDown: [10, 0] }[e.key];
    if (!step) return;
    e.preventDefault();
    rx = clampX(rx + step[0]);
    ry += step[1];
    vx = 0;
    vy = 0;
    idleSince = performance.now();
    wake();
  };
  el.addEventListener('pointerdown', onDown);
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  el.addEventListener('keydown', onKey);

  resize();
  render();
  el.classList.add('is-ready');
  wake();

  return {
    setTheme: (next) => {
      const t = next === 'light' ? 'light' : 'dark';
      if (t === theme) return;
      fade = { from: theme, to: t, start: performance.now() };
      theme = t;
      wake();
    },
    destroy: () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      el.removeEventListener('keydown', onKey);
      el.classList.remove('is-ready', 'is-dragging');
      geo.dispose();
      mat.dispose();
      env.dark.dispose();
      env.light.dispose();
      pmrem.dispose();
      renderer.dispose();
      renderer.forceContextLoss();   // libera el contexto WebGL ya, no al recolectar basura
      renderer.domElement.remove();
    },
  };
};

const Logo3D = ({ className = '' }) => {
  const { theme } = useTheme();
  const boxRef = useRef(null);
  const engineRef = useRef(null);
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    let cancelled = false;
    let destroy = () => {};
    Promise.all([
      import('three'),
      import('three/examples/jsm/utils/BufferGeometryUtils.js'),
      import('../lib/logoShapes'),
    ])
      .then(([THREE, utils, shapesModule]) => {
        if (cancelled || !boxRef.current) return;
        try {
          const engine = createEngine(boxRef.current, THREE, utils.toCreasedNormals, shapesModule.default.shapes, themeRef.current);
          engineRef.current = engine;
          destroy = engine.destroy;
        } catch (_) {
          // Sin WebGL: se queda la imagen fija del logo.
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      destroy();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setTheme(theme);
  }, [theme]);

  return (
    <div
      ref={boxRef}
      className={`q-logo3d ${className}`}
      role="img"
      aria-label="Logo QUINLATE SHOP en 3D. Arrástralo o usa las flechas para girarlo."
      tabIndex={0}
      data-testid="logo-3d"
    >
      <img src="/assets/logo-shop-tinta.webp" alt="" className="q-logo3d__fallback" draggable={false} />
    </div>
  );
};

export default Logo3D;
